import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react";
import {
  formatDistance as formatDistanceRaw,
  formatDistanceStat as formatDistanceStatRaw,
  formatDistanceChip as formatDistanceChipRaw,
  formatPace as formatPaceRaw,
  formatElevation as formatElevationRaw,
  type DistanceUnit
} from "../lib/units";
import { useAuthOptional } from "./AuthProvider";
import { loadPreferences, saveDistanceUnit } from "../lib/preferencesApi";

/**
 * Measurement preference (miles vs kilometres). The SPA stores all distances in
 * km / elevation in metres internally; this preference only changes how those
 * stored values are *shown* — never how they're persisted. Pace (`/mi` vs `/km`)
 * and elevation (`ft` vs `m`) follow the same switch for one consistent system.
 *
 * Persistence is two-tier:
 *   • `localStorage` is the instant first-paint cache for everyone (so distances
 *     never flash the wrong unit before auth/server resolve). Default is miles.
 *   • For a signed-in **customer** the server `CustomerPreferences.distanceUnit`
 *     is the source of truth — hydrated on load and written on change — so the
 *     choice follows them across devices. Guests stay on the local cache only.
 */
const STORAGE_KEY = "fg.units.distance";
const DEFAULT_UNIT: DistanceUnit = "mi";

function readStoredUnit(): DistanceUnit {
  if (typeof window === "undefined") return DEFAULT_UNIT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === "km" || raw === "mi" ? raw : DEFAULT_UNIT;
  } catch {
    // localStorage can throw in private-mode / blocked-cookie contexts.
    return DEFAULT_UNIT;
  }
}

function writeStoredUnit(unit: DistanceUnit): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, unit);
  } catch {
    // Non-fatal: preference simply won't persist locally this session.
  }
}

type UnitsContextValue = {
  distanceUnit: DistanceUnit;
  setDistanceUnit: (unit: DistanceUnit) => void;
};

const UnitsContext = createContext<UnitsContextValue>({
  distanceUnit: DEFAULT_UNIT,
  setDistanceUnit: () => {}
});

export function UnitsProvider({ children }: { children: ReactNode }) {
  const [distanceUnit, setDistanceUnitState] = useState<DistanceUnit>(readStoredUnit);
  const auth = useAuthOptional();
  const authKind = auth?.state.kind;

  // Hydrate from the server once a customer session resolves — the account's
  // saved unit wins over the local cache so the preference is consistent across
  // devices. Uses the raw state setter (not the persisting setter below) so this
  // read never echoes back as a server write.
  useEffect(() => {
    if (authKind !== "customer") return;
    let cancelled = false;
    loadPreferences()
      .then((res) => {
        const u = res.preferences?.distanceUnit;
        if (!cancelled && (u === "km" || u === "mi")) {
          setDistanceUnitState(u);
          writeStoredUnit(u);
        }
      })
      .catch(() => {
        // Offline / transient — keep the local cache value.
      });
    return () => {
      cancelled = true;
    };
  }, [authKind]);

  const setDistanceUnit = useCallback(
    (unit: DistanceUnit) => {
      setDistanceUnitState(unit);
      writeStoredUnit(unit);
      // Persist to the account when signed in; guests rely on the local cache.
      if (auth && auth.state.kind === "customer") {
        void saveDistanceUnit(unit, auth.state.csrfToken, auth.refreshCsrf).catch(() => {
          // Best-effort: the optimistic local update already took effect.
        });
      }
    },
    [auth]
  );

  const value = useMemo(
    () => ({ distanceUnit, setDistanceUnit }),
    [distanceUnit, setDistanceUnit]
  );

  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>;
}

/** Read the current unit + setter. */
export function useUnits(): UnitsContextValue {
  return useContext(UnitsContext);
}

/**
 * Formatters pre-bound to the current unit, so components don't have to thread
 * `distanceUnit` through every call. Module-scope/pure builders that can't call
 * hooks should instead take a `unit` argument and call the raw `lib/units`
 * formatters directly.
 */
export function useUnitFormat() {
  const { distanceUnit } = useUnits();
  return useMemo(
    () => ({
      distanceUnit,
      formatDistance: (km: number | null | undefined) => formatDistanceRaw(km, distanceUnit),
      formatDistanceStat: (km: number | null | undefined) => formatDistanceStatRaw(km, distanceUnit),
      formatDistanceChip: (km: number) => formatDistanceChipRaw(km, distanceUnit),
      formatPace: (km: number | null | undefined, durationMin: number) =>
        formatPaceRaw(km, durationMin, distanceUnit),
      formatElevation: (m: number | null | undefined) => formatElevationRaw(m, distanceUnit)
    }),
    [distanceUnit]
  );
}
